import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Button,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import { toast } from "react-toastify";
import { getId } from "../../utils/helpers/custom";
import * as Yup from "yup";
import { useFormik } from "formik";
import BreadCrumb from "../../components/common/breadCrumb";
import {
  addAcquireCompOfLeave,
  editDeleteAquireLeave,
} from "../../api/leaveApi";
import TableContainer from "../../components/common/tableContainer";
import { RequiredField } from "../../utils/helpers/validationMasseges";
import { dateFormat, errorHandler, getStatusBadgeClass } from "../../utils/helpers/common";
import { ResponseStatusEnum } from "../../utils/helpers/enums";
import ApproveRejectModal from "../../components/common/approveRejectModal";
import NoResultsMessage from "../../components/noResultMessage";
import { listOfData } from "../../api/commonApi";
import moment from "moment";
import BaseInput from "../../base/baseInput";
import {
  AQUIRE_LEAVE,
  CANT_DELETE,
  COMP_OFF_LEAVE,
  DATES,
  DELETE_LEAVE,
  PROCESSING,
  REASON,
  UPDATE,
} from "../../common/constant/Leave";

const CompoffLeave = () => {
  document.title = "Comp Off Leaves";
  const id = getId();
  const [loading, setLoading] = useState(false);
  const [isApproveRejectModalOpen, setApproveRejectModalOpen] = useState(false);
  const [listOfCompOffData, setListOfCompOffData] = useState([]);
  const [showAcquireModal, setShowAcquireModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editItemId, setEditItemId] = useState(null);

  const listDataOfCompOfLeave = useCallback(() => {
    setLoading(true);
    listOfData({
      modelName: "compOfLeaveModel",
      condition: {
        userId: id,
        isDeleted: false,
        leaveStatus: ["Pending", "Rejected", "Approved"],
      },
      selectionCriteria: ["id", "date", "reason", "status"],
    })
      .then((response) => {
        setListOfCompOffData(response?.data || []);
      })
      .catch((err) => {
        toast.error("Failed to fetch leaves");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  useEffect(() => {
    listDataOfCompOfLeave();
  }, [listDataOfCompOfLeave]);

  const handleDeleteLeave = () => {
    setLoading(true);
    const payload = { isDeleted: true };
    editDeleteAquireLeave(editItemId, payload)
      .then((response) => {
        if (response?.data?.statusCode === ResponseStatusEnum?.SUCCESS) {
          toast.success(response?.data?.message);
          listDataOfCompOfLeave();
        } else {
          toast.error(response?.data?.message);
        }
      })
      .catch((err) => {
        errorHandler(err);
      })
      .finally(() => {
        setLoading(false);
        setApproveRejectModalOpen(false);
        setEditItemId(null);
      });
  };

  const handleEditClick = (id) => {
     AcquireLeaveValidation.resetForm();
    AcquireLeaveValidation.setValues({
      date: moment(id?.date).format("YYYY-MM-DD"),
      reason: id?.reason || "",
    });
    setEditItemId(id);
    setIsEdit(true);
    setShowAcquireModal(true);
  };

  const AcquireLeaveValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      date: "",
      reason: "",
    },
    validationSchema: Yup.object().shape({
      date: Yup.string().required(RequiredField(DATES)),
      reason: Yup.string().required(RequiredField(REASON)),
    }),
    onSubmit: (values, { resetForm }) => {
      setLoading(true);
      const payload = {
        date: moment(values.date).format("YYYY-MM-DD"),
        reason: values.reason,
        userId: id,
        status: "Pending",
      };
      console.log("editItemId", editItemId);
      
      const apiCall = isEdit
        ? editDeleteAquireLeave(editItemId?.id, payload)
        : addAcquireCompOfLeave(payload);

      apiCall
        .then((res) => {
          if (res?.data?.statusCode === ResponseStatusEnum.SUCCESS) {
            toast.success(res?.data?.message);
            listDataOfCompOfLeave();
            setShowAcquireModal(false);
            resetForm();
            setIsEdit(false);
            setEditItemId(null);
          } else {
            toast.error(res?.data?.message);
          }
        })
        .catch((err) => {
          errorHandler(err);
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  const leaveColumns = useMemo(
    () => [
      {
        Header: "Date",
        accessor: "date",
        Cell: ({ value }) => (value ? dateFormat(value) : "-"),
      },
      {
        Header: "Reason",
        accessor: "reason",
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => (
          <span
            className={`badge badge-soft-${getStatusBadgeClass(
              value
            )} text-uppercase`}
          >
            {value}
          </span>
        ),
      },
      {
        Header: "Action",
        Cell: ({ row }) => {
          const isDisabled =
            row.original.status === "Approved" ||
            row.original.status === "Rejected";

          return (
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-soft-danger"
                disabled={isDisabled}
                onClick={() => {
                  setEditItemId(row.original.id);
                  setApproveRejectModalOpen(true);
                }}
                title={isDisabled ? CANT_DELETE : DELETE_LEAVE}
              >
                <i className="ri-delete-bin-line" />
              </button>
              <button
                className="btn btn-sm btn-soft-info"
                disabled={isDisabled}
                onClick={() => handleEditClick(row.original)}
                title={
                  isDisabled
                    ? "Cannot edit approved or rejected leaves"
                    : "Edit leave"
                }
              >
                <i className="ri-pencil-fill" />
              </button>
            </div>
          );
        },
      },
    ],
    []
  );

  const handleModalClose = () => {
    setShowAcquireModal(false);
    setIsEdit(false);
    setEditItemId(null);
    AcquireLeaveValidation.resetForm();
  };

  return (
    <React.Fragment>
      <ApproveRejectModal
        show={isApproveRejectModalOpen}
        onCloseClick={() => setApproveRejectModalOpen(false)}
        onDeleteClick={handleDeleteLeave}
        action="delete leave"
      />

      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Comp Off Acquire Leaves"
            pageTitle="Comoff Leaves"
            navigation="/leaves"
          />

          <div className="container-fluid">
            <Row className="mb-4 d-flex justify-content-between align-items-center">
              <Col lg={6} className="text-start">
                <h5 className="card-title mb-0">Comp Off Leave Requests</h5>
              </Col>
              <Col lg={6} className="text-end">
                <Button
                  color="primary"
                  onClick={() => {
                    setIsEdit(false);
                    setEditItemId(null);
                    AcquireLeaveValidation.resetForm();
                    setShowAcquireModal(true);
                  }}
                >
                  <i className="ri-add-line align-bottom me-1" /> Aquire Leave
                </Button>
              </Col>
            </Row>
          </div>
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  {loading || listOfCompOffData?.length > 0 ? (
                    <TableContainer
                      columns={leaveColumns}
                      data={listOfCompOffData}
                      tableLoader={loading}
                      customPageSize={10}
                    />
                  ) : (
                    <NoResultsMessage />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        <Modal isOpen={showAcquireModal} toggle={handleModalClose} centered>
          <ModalHeader toggle={handleModalClose}>
            {isEdit ? "Edit " + COMP_OFF_LEAVE : "Add " + COMP_OFF_LEAVE}
          </ModalHeader>
          <ModalBody>
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                AcquireLeaveValidation.handleSubmit();
              }}
            >
              <div className="mb-3">
                <Label>
                  Date <span className="text-danger"> *</span>
                </Label>
                <Flatpickr
                  className={`form-control ${
                    AcquireLeaveValidation.errors.date &&
                    AcquireLeaveValidation.touched.date
                      ? "is-invalid"
                      : ""
                  }`}
                  name="date"
                  id="date-field"
                  value={AcquireLeaveValidation.values.date}
                  onChange={([date]) =>
                    AcquireLeaveValidation.setFieldValue("date", date)
                  }
                  options={{
                    dateFormat: "Y-m-d",
                    maxDate: new Date(),
                    altInput: true,
                  }}
                  disabled={isEdit}
                />
                {AcquireLeaveValidation.errors.date &&
                  AcquireLeaveValidation.touched.date && (
                    <div className="invalid-feedback">
                      {AcquireLeaveValidation.errors.date}
                    </div>
                  )}
              </div>

              <BaseInput
                label={REASON}
                name="reason"
                type="textarea"
                value={AcquireLeaveValidation.values.reason}
                onChange={AcquireLeaveValidation.handleChange}
                onBlur={AcquireLeaveValidation.handleBlur}
                error={AcquireLeaveValidation.errors.reason}
                touched={AcquireLeaveValidation.touched.reason}
                rows={4}
                required={true}
              />

              <div className="d-flex justify-content-end gap-2">
                <Button color="light" onClick={handleModalClose}>
                  Cancel
                </Button>
                <Button type="submit" color="primary" disabled={loading}>
                  {loading ? PROCESSING : isEdit ? UPDATE : AQUIRE_LEAVE}
                </Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default CompoffLeave;
